import { useState, useCallback } from 'react'
import { lumi } from '@/lib/lumi'
import type { InterviewResult, InterviewStatus } from '@/lib/recruitment'

export interface InterviewResponse {
  fieldId: string
  label: string
  value: string
}

export interface CompetencyScore {
  name: string
  score: number
}

export interface Interview {
  _id: string
  companyUserId: string
  companyName?: string
  collaboratorUserId: string
  collaboratorName?: string
  collaboratorEmail?: string
  collaboratorPhoto?: string
  vacancyId?: string
  vacancyTitle?: string
  applicationId?: string
  templateId?: string
  templateName?: string
  stage?: string
  interviewer?: string
  interviewerUserId?: string
  scheduledDate?: string
  status: InterviewStatus
  responses?: InterviewResponse[]
  observations?: string
  strengths?: string
  opportunities?: string
  recommendations?: string
  privateNotes?: string
  overallScore?: number
  scaleType?: '10' | '100'
  competencyScores?: CompetencyScore[]
  result?: InterviewResult
  creator?: string
  createdAt: string
  updatedAt: string
}

export function useInterviews() {
  const [interviews, setInterviews] = useState<Interview[]>([])
  const [loading, setLoading] = useState(false)

  const fetchByCompany = useCallback(async (companyUserId: string) => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.interviews.list({
        filter: { companyUserId },
        sort: { createdAt: -1 },
      })
      setInterviews(list as Interview[])
    } catch (e) {
      console.error('Error al cargar entrevistas (empresa):', e)
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchByCollaborator = useCallback(async (collaboratorUserId: string) => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.interviews.list({
        filter: { collaboratorUserId },
        sort: { createdAt: -1 },
      })
      setInterviews(list as Interview[])
    } catch (e) {
      console.error('Error al cargar entrevistas (candidato):', e)
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchAll = useCallback(async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.interviews.list({
        sort: { createdAt: -1 },
      })
      setInterviews(list as Interview[])
    } catch (e) {
      console.error('Error al cargar entrevistas:', e)
    } finally {
      setLoading(false)
    }
  }, [])

  const createInterview = useCallback(async (data: Partial<Interview>) => {
    const now = new Date().toISOString()
    return lumi.entities.interviews.create({
      ...data,
      status: data.status || 'programada',
      createdAt: now,
      updatedAt: now,
    })
  }, [])

  const updateInterview = useCallback(async (id: string, data: Partial<Interview>) => {
    return lumi.entities.interviews.update(id, {
      ...data,
      updatedAt: new Date().toISOString(),
    })
  }, [])

  const deleteInterview = useCallback(async (id: string) => {
    return lumi.entities.interviews.delete(id)
  }, [])

  return {
    interviews,
    loading,
    fetchByCompany,
    fetchByCollaborator,
    fetchAll,
    createInterview,
    updateInterview,
    deleteInterview,
  }
}
